import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, Steps, Table } from 'antd';
import { PlayerInviteModalBody, PlayerInviteModalButtons } from 'components/PlayerInviteModalBody';
import { useCallback, useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';
import { bulkInviteToSeason } from 'utils/apiFunctions';
import SeasonSelectionTable from './SeasonSelectionTable';

const InvitePlayerToSeasonDialog = ({
  open,
  onClose,
  player,
  onSuccess,
}) => {
  const STEPS = {
    SELECT_SEASON: 0,
    RESULTS: 1,
  };

  const [step, setStep] = useState(STEPS.SELECT_SEASON);
  const currentRole = useLoginStore(s => s.getCurrentRole)();
  const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteResults, setInviteResults] = useState(null);
  const addMessage = useNotificationStore((state) => state.addMessage);

  const handleSendInvites = async () => {
    if (selectedSeasonIds.length === 0) {
      message.warning('Please select one season.');
      return;
    }

    Modal.confirm({
      title: 'Confirm sending invite',
      content: `Are you sure you want to send invite to ${player?.firstName} ${player?.lastName} for the selected season?`,
      okText: 'Yes, send invite',
      cancelText: 'Cancel',
      okButtonProps: { size: 'small' },
      cancelButtonProps: { size: 'small' },
      onOk: async () => {
        setLoading(true);
        setInviteResults(null);
        try {
          const playerIds = [player.playerId];
          const res = await bulkInviteToSeason({ playerIds, seasonIds: selectedSeasonIds }, currentRole);
          const json = await res.json();

          setInviteResults(
            (json.results || null)
              ? json.results.map(r => {
                return {
                  ...r,
                  firstName: player?.firstName || '',
                  lastName: player?.lastName || '',
                  email: r.email || '',
                  seasonName: r.seasonName || r.seasonId,
                };
              })
              : null
          );

          // Always advance to results step
          setStep(STEPS.RESULTS);


        } catch (e) {
          addMessage({ text: 'Error communicating with the server.', type: 'error' });
          // Still show results step even on error
          setStep(STEPS.RESULTS);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancel = useCallback(() => {
    // Only call onSuccess if there was a successful invitation
    onSuccess && onSuccess();
    onClose();
    setStep(STEPS.SELECT_SEASON);
    setSelectedSeasonIds([]);
    setInviteResults(null);
  }, [onClose, onSuccess]);

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="Invite Player to Season"
      footer={null}
      width={typeof window !== 'undefined' && window.innerWidth < 700 ? '95vw' : (window.innerWidth < 1400 ? '85vw' : 1400)}
      style={{
        top: '12vh',
        // height: '85vh',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
      }}
      bodyStyle={{
        // minHeight: '300px',
        // height: 'calc(100vh - 200px)', // 110px for header/footer
        display: 'flex',
        flexDirection: 'column',
        padding: typeof window !== 'undefined' && window.innerWidth < 700 ? 8 : 24,
        paddingBottom: 0,
        overflow: 'hidden',
      }} 
    >
      <div className="px-2 pt-2">
        <Steps current={step} className="mb-4" size="small">
          <Steps.Step title="Select Season" />
          <Steps.Step title="Results" />
        </Steps>
      </div>
      <div className="flex-1 flex flex-col min-h-0">
        {step === STEPS.SELECT_SEASON && (
          <>
          <PlayerInviteModalBody>
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
              <SeasonSelectionTable
                className="mb-6"
                open={open}
                filters={{}}
                selectedRowKeys={selectedSeasonIds}
                onChange={setSelectedSeasonIds}
                header={
                  <div className="flex gap-2 items-center justify-start text-gray-600 ml-2">
                    <InfoCircleOutlined />
                    Only Seasons with 'Player Registration' set to Enabled and status 'Open for registration' or 'Running' are shown.
                  </div>
                }
              />
            </div>
            </PlayerInviteModalBody>
            <PlayerInviteModalButtons>
              <Button 
                size="small" 
                onClick={handleCancel}>Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                disabled={selectedSeasonIds.length === 0}
                loading={loading}
                onClick={handleSendInvites}
              >
                Send invite
              </Button>
            </PlayerInviteModalButtons>
          </>
        )}
        {step === STEPS.RESULTS && (
          <>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <h4 className="mt-4">Invite Results</h4>
              {inviteResults ? (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  <Table
                    className="mb-6"
                    rowKey={(r) => (r.playerId || '') + (r.email || '') + (r.seasonId || '')}
                    columns={[
                      { title: 'First name', dataIndex: 'firstName', key: 'firstName' },
                      { title: 'Last name', dataIndex: 'lastName', key: 'lastName' },
                      { title: 'Email', dataIndex: 'email', key: 'email' },
                      { title: 'Season', dataIndex: 'seasonName', key: 'seasonName' },
                      {
                        title: 'Status', dataIndex: 'status', key: 'status',
                        render: (status) => {
                          if (status === 'invited-new-user' || status === 'invited-existing-user') return <span style={{ color: 'green' }}>Invited</span>;
                          if (status === 'error') return <span style={{ color: 'red' }}>Error</span>;
                          return status;
                        }
                      },
                      { title: 'Message', dataIndex: 'message', key: 'message' },
                    ]}
                    dataSource={inviteResults}
                    pagination={false}
                    size="small"
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {loading ? 'Processing invites...' : 'No results available'}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 left-0 w-full bg-white z-10 flex justify-end gap-2 border-t border-gray-200 p-4">
              <Button size="small" type="primary" onClick={handleCancel}>Close</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default InvitePlayerToSeasonDialog; 